import { httpClient } from "../http-client";
import { extractApiData } from "./response-utils";
import { buildQueryString } from "./query-builder";
import {
  AIContentGenerationRequest,
  AIContentGenerationResponse,
  AIAudienceAnalysisRequest,
  AIAudienceAnalysisResponse,
  AIAttendancePredictionRequest,
  AIAttendancePredictionResponse,
  AIHealthStatus,
  AIInsightsResponse,
  AIMarketingCopyRequest,
  AIMarketingCopyResponse,
  AIOptimizeEventRequest,
  AIOptimizeEventResponse,
  AIRecommendationsRequest,
  AIRecommendationsResponse,
  AISentimentAnalysisRequest,
  AISentimentAnalysisResponse,
  AISmartSchedulingRequest,
  AISmartSchedulingResponse,
  AISuggestionsRequest,
  AISuggestionsResponse,
  AIBudgetOptimizationRequest,
  AIBudgetOptimizationResponse,
  AIDescriptionRequest,
  AIDescriptionResponse,
  AITemplatesResponse,
  IBackendChatResult,
  IBackendSupportSession,
  ICreateSupportSessionPayload,
  IGetSupportSessionsParams,
  ISupportEscalationResult,
} from "../../types/support";

export const aiSupportApi = {
  async getHealth(): Promise<AIHealthStatus> {
    const response = await httpClient.get<any>("/ai-support/actions/ai-health");
    return extractApiData<AIHealthStatus>(response);
  },

  async getSuggestions(
    payload: AISuggestionsRequest,
  ): Promise<AISuggestionsResponse> {
    const response = await httpClient.post<any>(
      "/ai-support/actions/suggestions",
      payload,
    );
    return extractApiData<AISuggestionsResponse>(response);
  },

  async getSuggestionsByContext(
    context: string,
    params: Partial<AISuggestionsRequest> = {},
  ): Promise<AISuggestionsResponse> {
    return this.getSuggestions({ context, ...params });
  },

  async getInvitationSuggestions(
    eventId: string,
  ): Promise<AISuggestionsResponse> {
    return this.getSuggestions({
      context: "invitations",
      eventData: { eventId },
    });
  },

  async generateContent(
    payload: AIContentGenerationRequest,
  ): Promise<AIContentGenerationResponse> {
    const response = await httpClient.post<any>(
      "/ai-support/actions/content-generation",
      payload,
    );
    return extractApiData<AIContentGenerationResponse>(response);
  },

  async getRecommendations(
    payload: AIRecommendationsRequest,
  ): Promise<AIRecommendationsResponse> {
    const response = await httpClient.post<any>(
      "/ai-support/actions/recommendations",
      payload,
    );
    return extractApiData<AIRecommendationsResponse>(response);
  },

  async optimizeEvent(
    payload: AIOptimizeEventRequest,
  ): Promise<AIOptimizeEventResponse> {
    const response = await httpClient.post<any>(
      "/ai-support/actions/optimize-event",
      payload,
    );
    return extractApiData<AIOptimizeEventResponse>(response);
  },

  async generateDescription(
    payload: AIDescriptionRequest,
  ): Promise<AIDescriptionResponse> {
    const response = await httpClient.post<any>(
      "/ai-support/actions/generate-description",
      payload,
    );
    return extractApiData<AIDescriptionResponse>(response);
  },

  async getTemplates(type?: string): Promise<AITemplatesResponse> {
    const response = await httpClient.get<any>(
      `/ai-support/actions/templates${type ? buildQueryString({ type }) : ""}`,
    );
    return extractApiData<AITemplatesResponse>(response);
  },

  async analyzeSentiment(
    payload: AISentimentAnalysisRequest,
  ): Promise<AISentimentAnalysisResponse> {
    const response = await httpClient.post<any>(
      "/ai-support/actions/analyze-sentiment",
      payload,
    );
    return extractApiData<AISentimentAnalysisResponse>(response);
  },

  async smartScheduling(
    payload: AISmartSchedulingRequest,
  ): Promise<AISmartSchedulingResponse> {
    const response = await httpClient.post<any>(
      "/ai-support/actions/smart-scheduling",
      payload,
    );
    return extractApiData<AISmartSchedulingResponse>(response);
  },

  async audienceAnalysis(
    payload: AIAudienceAnalysisRequest,
  ): Promise<AIAudienceAnalysisResponse> {
    const response = await httpClient.post<any>(
      "/ai-support/actions/audience-analysis",
      payload,
    );
    return extractApiData<AIAudienceAnalysisResponse>(response);
  },

  async budgetOptimization(
    payload: AIBudgetOptimizationRequest,
  ): Promise<AIBudgetOptimizationResponse> {
    const response = await httpClient.post<any>(
      "/ai-support/actions/budget-optimization",
      payload,
    );
    return extractApiData<AIBudgetOptimizationResponse>(response);
  },

  async getUserInsights(period?: string): Promise<AIInsightsResponse> {
    const response = await httpClient.get<any>(
      `/ai-support/insights${buildQueryString({ period })}`,
    );
    return extractApiData<AIInsightsResponse>(response);
  },

  async predictAttendance(
    payload: AIAttendancePredictionRequest,
  ): Promise<AIAttendancePredictionResponse> {
    const response = await httpClient.post<any>(
      "/ai-support/actions/predict-attendance",
      payload,
    );
    return extractApiData<AIAttendancePredictionResponse>(response);
  },

  async generateMarketingCopy(
    payload: AIMarketingCopyRequest,
  ): Promise<AIMarketingCopyResponse> {
    const response = await httpClient.post<any>(
      "/ai-support/actions/generate-marketing-copy",
      payload,
    );
    return extractApiData<AIMarketingCopyResponse>(response);
  },

  async getTemplateSuggestions(
    eventId: string,
    type?: string,
  ): Promise<AISuggestionsResponse> {
    return this.getSuggestions({
      context: "templates",
      eventData: { eventId },
      ...(type ? { metadata: { type } as Record<string, any> } : {}),
    });
  },

  // ==================== SESSION MANAGEMENT (Chat alternative) ====================
  // Тела ниже сверены с CreateSupportSessionDto: глобальный ValidationPipe включён с
  // forbidNonWhitelisted, поэтому любое лишнее поле — не «проигнорируется», а 400.
  // Владельца сессии бэкенд ставит из токена (req.user.id) — userId слать НЕЛЬЗЯ.
  async createSession(
    payload: ICreateSupportSessionPayload,
  ): Promise<IBackendSupportSession> {
    const response = await httpClient.post<any>(
      "/ai-support/sessions",
      payload,
    );
    return extractApiData<IBackendSupportSession>(response);
  },

  async submitFeedback(payload: {
    reason: string;
    expectedOutcome?: string;
    sessionId?: string;
    lastAiMessage?: string;
    consent?: boolean;
  }) {
    const response = await httpClient.post<any>(
      "/ai-support/feedback",
      payload,
    );
    return extractApiData(response);
  },

  // SendMessageDto = { content, type?, metadata? }. Поля `sender` в нём нет:
  // отправителя бэкенд проставляет сам (MessageSender.USER), а лишнее поле = 400.
  async sendMessage(
    sessionId: string,
    message: string,
  ): Promise<IBackendChatResult> {
    const response = await httpClient.post<any>(
      `/ai-support/sessions/${sessionId}/messages`,
      {
        content: message,
      },
    );
    return extractApiData<IBackendChatResult>(response);
  },

  async getSession(sessionId: string) {
    const response = await httpClient.get<any>(
      `/ai-support/sessions/${sessionId}`,
    );
    return extractApiData(response);
  },

  // GetSessionsDto знает page/limit/sessionType/category/search. Прежние
  // `status` и `offset` бэкенду неизвестны — это был запрос, отвечавший 400.
  async getUserSessions(params?: IGetSupportSessionsParams) {
    const response = await httpClient.get<any>(
      `/ai-support/sessions${buildQueryString(params || {})}`,
    );
    return extractApiData(response);
  },

  async closeSession(sessionId: string) {
    const response = await httpClient.delete<any>(
      `/ai-support/sessions/${sessionId}`,
    );
    return extractApiData(response);
  },

  async escalateSession(
    sessionId: string,
    reason: string,
  ): Promise<ISupportEscalationResult> {
    const response = await httpClient.post<any>(
      `/ai-support/sessions/${sessionId}/escalate`,
      {
        reason,
      },
    );
    return extractApiData<ISupportEscalationResult>(response);
  },

  async resolveSession(sessionId: string) {
    const response = await httpClient.put<any>(
      `/ai-support/sessions/${sessionId}/resolve`,
      {},
    );
    return extractApiData(response);
  },

  async rateSession(sessionId: string, rating: number, feedback?: string) {
    const response = await httpClient.post<any>(
      `/ai-support/sessions/${sessionId}/rate`,
      {
        rating,
        feedback,
      },
    );
    return extractApiData(response);
  },

  // ==================== KNOWLEDGE BASE ====================
  async searchKnowledgeBase(params: {
    query: string;
    category?: string;
    limit?: number;
  }) {
    const response = await httpClient.get<any>(
      `/ai-support/knowledge-base/search${buildQueryString(params)}`,
    );
    return extractApiData(response);
  },

  async getKnowledgeBaseCategories() {
    const response = await httpClient.get<any>(
      "/ai-support/knowledge-base/categories",
    );
    return extractApiData(response);
  },

  async getKnowledgeBaseArticle(articleId: string) {
    const response = await httpClient.get<any>(
      `/ai-support/knowledge-base/articles/${articleId}`,
    );
    return extractApiData(response);
  },

  // ==================== FAQ ====================
  async getFaq(params?: {
    category?: string;
    search?: string;
    limit?: number;
  }) {
    const response = await httpClient.get<any>(
      `/ai-support/faq${buildQueryString(params || {})}`,
    );
    return extractApiData(response);
  },

  // ==================== EVENT ANALYSIS ====================
  async analyzeEvent(eventId: string, analysisType?: string) {
    const response = await httpClient.post<any>("/ai-support/event-analysis", {
      eventId,
      analysisType,
    });
    return extractApiData(response);
  },

  // ==================== TASK OPTIMIZATION ====================
  async optimizeTasks(payload: { taskIds?: string[]; projectId?: string }) {
    const response = await httpClient.post<any>(
      "/ai-support/task-optimization",
      payload,
    );
    return extractApiData(response);
  },

  // ==================== TRAINING MATERIALS ====================
  async getTrainingMaterials(params?: {
    category?: string;
    level?: "beginner" | "intermediate" | "advanced";
  }) {
    const response = await httpClient.get<any>(
      `/ai-support/training${buildQueryString(params || {})}`,
    );
    return extractApiData(response);
  },

  // ==================== WRAPPER FUNCTIONS (for PROMPT_05 compatibility) ====================
  // These provide the specific function names expected by PROMPT_05 code examples
  // while using the generic backend endpoints

  // Event Recommendations wrappers
  async getEventRecommendations(userId?: string) {
    return this.getRecommendations({
      context: "events",
      ...(userId ? { userProfile: { userId } as Record<string, any> } : {}),
    });
  },

  async getPersonalizedSuggestions() {
    return this.getSuggestions({ context: "personalized" });
  },

  async getSimilarEvents(eventId: string) {
    return this.getRecommendations({ context: "similar", eventId });
  },

  async getTrendingEvents() {
    return this.getRecommendations({ context: "trending" });
  },

  // Task Automation wrappers
  async autoGenerateTasks(eventId: string) {
    return this.optimizeTasks({ projectId: eventId });
  },

  async suggestTaskAssignees(taskId: string) {
    return this.getSuggestions({
      context: "task-assignees",
      eventData: { taskId },
    });
  },

  async optimizeTaskSchedule(eventId: string) {
    return this.optimizeTasks({ projectId: eventId });
  },

  // Content Generation wrappers
  async generateEventDescription(prompt: string) {
    return this.generateContent({ type: "event-description", prompt });
  },

  async generateEmailTemplate(type: string, context: unknown) {
    return this.generateContent({
      type: "email-template",
      prompt: type,
      context: context as Record<string, any>,
    });
  },

  async generateSocialPost(eventId: string, platform: string) {
    return this.generateContent({
      type: "social-post",
      prompt: `Generate social post for event ${eventId}`,
      context: { eventId, platform },
    });
  },

  async improveText(text: string, style: string) {
    return this.generateContent({
      type: "improve-text",
      prompt: text,
      context: { style },
    });
  },

  async translateContent(text: string, targetLang: string) {
    return this.generateContent({
      type: "translate",
      prompt: text,
      context: { targetLang },
    });
  },

  // Analytics Insights wrappers
  async getEventInsights(eventId: string) {
    return this.getUserInsights(eventId);
  },

  async getPredictiveAnalytics(eventId: string) {
    return this.analyzeEvent(eventId, "predictive");
  },

  async getAnomalyDetection(metricType: string) {
    return this.getUserInsights(metricType);
  },

  async generateReport(params: unknown) {
    return this.generateContent({
      type: "report",
      prompt: "Generate report",
      context: params as Record<string, any>,
    });
  },

  async speechToText(
    audioBlob: Blob,
  ): Promise<{ text: string; confidence: number; language: string }> {
    const formData = new FormData();
    formData.append("audio", audioBlob, "recording.webm");
    const response = await httpClient.post<{
      text: string;
      confidence: number;
      language: string;
    }>("/ai-support/voice/speech-to-text", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return extractApiData(response);
  },

  async textToSpeech(
    text: string,
    voice?: string,
  ): Promise<{ audio: string; format: string; duration: number }> {
    const response = await httpClient.post<{
      audio: string;
      format: string;
      duration: number;
    }>("/ai-support/voice/text-to-speech", { text, voice });
    return extractApiData(response);
  },

  async voiceCommand(command: string) {
    const response = await httpClient.post<{
      intent: string;
      action: string;
      response: string;
      confidence: number;
    }>("/ai-support/voice/command", { command });
    return extractApiData(response);
  },
};

export default aiSupportApi;
